import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, FolderKanban, Package, Kanban, Home, Inbox, PenLine, Settings, CornerDownLeft, Ship } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { api } from "@/lib/api";
import { STATUS_LABEL, compactMoney } from "@/lib/sk";

const NAV = [
  { to: "/", label: "Hjem", icon: Home, key: "oversikt" },
  { to: "/ny", label: "Nytt arbeid", icon: PenLine, key: "home" },
  { to: "/innboks", label: "Innboks", icon: Inbox, key: "inbox" },
  { to: "/arbeid", label: "Arbeidsområder", icon: FolderKanban, key: "projects" },
  { to: "/pipeline", label: "Tilbudsoversikt", icon: Kanban, key: "pipeline" },
  { to: "/customers", label: "Kunder", icon: Building2, key: "customers" },
  { to: "/products", label: "Produkter og regler", icon: Package, key: "products" },
  { to: "/settings", label: "Innstillinger", icon: Settings, key: "settings" },
];

function Tile({ icon: Icon, accent }) {
  return (
    <span className={accent ? "flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-primary" : "flex h-7 w-7 items-center justify-center rounded-lg bg-black/[0.05] text-muted-foreground"}>
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

const has = (hay, s) => (hay || "").toLowerCase().includes(s);

/** ⌘K – jump to any project, customer or product. Everything is a view of the same project data. */
export function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ projects: [], accounts: [], products: [] });
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQ("");
      return;
    }
    Promise.all([api.get("/projects", { params: { limit: 200 } }), api.get("/accounts"), api.get("/products")])
      .then(([p, a, pr]) => setData({ projects: p.data || [], accounts: a.data || [], products: pr.data || [] }))
      .catch(() => {});
  }, [open]);

  const s = q.trim().toLowerCase();
  const hits = useMemo(() => {
    if (!s) return null;
    return {
      projects: data.projects.filter((p) => [p.name, p.customer?.name, p.vessel?.name, String(p.number)].some((x) => has(x, s))).slice(0, 6),
      accounts: data.accounts.filter((a) => [a.name, a.city, a.country, a.industry].some((x) => has(x, s))).slice(0, 5),
      products: data.products.filter((p) => [p.name, p.sku, p.category].some((x) => has(x, s))).slice(0, 5),
    };
  }, [s, data]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };
  const empty = hits && !hits.projects.length && !hits.accounts.length && !hits.products.length;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Søk i prosjekter, kunder, produkter – eller hopp til…" value={q} onValueChange={setQ} data-testid="command-palette-input" />
      <CommandList className="max-h-[440px]">
        {empty ? <CommandEmpty>Ingen treff. Prøv prosjektnummer, kunde, fartøy eller SKU.</CommandEmpty> : null}
        {!s ? (
          <>
            <CommandGroup heading="Start">
              <CommandItem onSelect={() => go("/")} data-testid="command-new-work">
                <Tile icon={PenLine} accent /> <span className="flex-1">Nytt arbeid</span><span className="text-xs text-muted-foreground">beskriv en forespørsel eller dra inn Excel</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Gå til">
              {NAV.map((n) => (
                <CommandItem key={n.to} onSelect={() => go(n.to)} data-testid={`command-nav-${n.key}`}>
                  <Tile icon={n.icon} /> <span className="flex-1">{n.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {data.projects.length ? (
              <>
                <CommandSeparator />
                <CommandGroup heading="Sist arbeidet med">
                  {data.projects.filter((p) => p.status !== "new").slice(0, 4).map((p) => (
                    <CommandItem key={p.id} value={`recent-${p.id}-${p.number}-${p.name}`} onSelect={() => go(`/p/${p.id}`)} data-testid={`command-recent-${p.number}`}>
                      <Tile icon={Ship} /><span className="font-mono text-[11px] text-muted-foreground">#{p.number}</span><span className="flex-1 truncate">{p.name}</span><span className="ml-3 truncate text-xs text-muted-foreground">{p.customer?.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            ) : null}
          </>
        ) : null}
        {hits?.projects.length ? (
          <CommandGroup heading="Prosjekter">
            {hits.projects.map((p) => (
              <CommandItem key={p.id} value={`p-${p.id}-${p.number}-${p.name} ${p.customer?.name || ""}`} onSelect={() => go(`/p/${p.id}`)} data-testid="search-result-project">
                <Tile icon={Ship} /><span className="font-mono text-[11px] text-muted-foreground">#{p.number}</span><span className="flex-1 truncate">{p.name}</span>
                <span className="ml-3 truncate text-xs text-muted-foreground">{STATUS_LABEL[p.status] || p.status}{p.value ? ` · ${compactMoney(p.value)}` : ""}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {hits?.accounts.length ? (
          <CommandGroup heading="Kunder">
            {hits.accounts.map((a) => (
              <CommandItem key={a.id} value={`a-${a.id}-${a.name}`} onSelect={() => go(`/customers/${a.id}`)} data-testid="search-result-account">
                <Tile icon={Building2} /><span className="flex-1 truncate">{a.name}</span><span className="ml-3 truncate text-xs text-muted-foreground">{[a.industry, a.city, a.country].filter(Boolean).join(" · ")}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {hits?.products.length ? (
          <CommandGroup heading="Produkter">
            {hits.products.map((p) => (
              <CommandItem key={p.id} value={`pr-${p.id}-${p.name}`} onSelect={() => go(`/products?q=${encodeURIComponent(p.sku || p.name)}`)} data-testid="search-result-product">
                <Tile icon={Package} /><span className="flex-1 truncate">{p.name}</span><span className="ml-3 truncate font-mono text-xs text-muted-foreground">{p.sku}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
      <div className="flex items-center justify-between border-t border-black/[0.06] bg-[hsl(210_20%_98%)] px-4 py-2 text-[11.5px] text-muted-foreground">
        <span className="flex items-center gap-3"><span className="flex items-center gap-1"><span className="kbd">↑</span><span className="kbd">↓</span> naviger</span><span className="flex items-center gap-1"><span className="kbd"><CornerDownLeft className="h-3 w-3" /></span> åpne</span></span>
        <span className="flex items-center gap-1"><span className="kbd">esc</span> lukk</span>
      </div>
    </CommandDialog>
  );
}
